<template>
  <div>
    <b-row>
      <b-col md="12">

        <!-- ── Patient Info Card (from first card) ──────────────────── -->
        <b-card
          v-if="primaryCard"
          no-body
          class="info-card mb-3"
        >
          <b-card-header class="section-header">
            <h5 class="mb-0">
              <feather-icon
                icon="UserIcon"
                size="18"
                class="mr-2"
              />
              {{ $t("reception.patient-card-data") }}
              <b-badge
                variant="primary"
                class="ml-2"
              >
                {{ primaryCard.type }}
              </b-badge>
              <b-badge
                :variant="primaryCard.is_active ? 'success' : 'danger'"
                class="ml-1"
              >
                {{ primaryCard.is_active ? $t("common.active") : $t("common.inactive") }}
              </b-badge>
            </h5>
          </b-card-header>
          <b-card-body class="p-2">
            <div class="info-list">

              <!-- Patient -->
              <div
                v-if="primaryCard.patient && primaryCard.patient.name"
                class="info-row"
              >
                <label class="info-label">{{ $t("common.name") }}</label>
                <p class="info-value">
                  {{ primaryCard.patient.name }}
                </p>
              </div>
              <div
                v-if="primaryCard.card_no"
                class="info-row"
              >
                <label class="info-label">{{ $t("common.card_no") }}</label>
                <p class="info-value">
                  {{ primaryCard.card_no }}
                </p>
              </div>

              <!-- Companion -->
              <div class="info-section-divider" />
              <div class="info-section-title">
                <feather-icon
                  icon="UsersIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("reception.companion-information") }}
              </div>
              <div
                v-if="primaryCard.full_companion_name"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.companion-name") }}</label>
                <p class="info-value">
                  {{ primaryCard.full_companion_name }}
                </p>
              </div>
              <div
                v-if="primaryCard.companion_phone"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.companion-phone") }}</label>
                <p
                  class="info-value"
                  style="unicode-bidi:isolate"
                  :style="{ direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr' }"
                >
                  {{ primaryCard.companion_phone }}
                </p>
              </div>
              <div
                v-if="primaryCard.child_name"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.child-name") }}</label>
                <p class="info-value">
                  {{ primaryCard.child_name }}
                </p>
              </div>

              <!-- Next of Kin -->
              <div class="info-section-divider" />
              <div class="info-section-title">
                <feather-icon
                  icon="UserCheckIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("reception.next-of-kin") }}
              </div>
              <div
                v-if="primaryCard.next_of_kin_name"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.next-kin-name") }}</label>
                <p class="info-value">
                  {{ primaryCard.next_of_kin_name }}
                </p>
              </div>
              <div
                v-if="primaryCard.next_of_kin_phone_number"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.next-kin-phone") }}</label>
                <p
                  class="info-value"
                  style="unicode-bidi:isolate"
                  :style="{ direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr' }"
                >
                  {{ primaryCard.next_of_kin_phone_number }}
                </p>
              </div>

              <!-- Medical -->
              <div class="info-section-divider" />
              <div class="info-section-title">
                <feather-icon
                  icon="ActivityIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("reception.medical-information") }}
              </div>
              <div
                v-if="primaryCard.weight"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.weight") }}</label>
                <p class="info-value">
                  {{ primaryCard.weight }} {{ $t("common.kg") || "kg" }}
                </p>
              </div>
              <div
                v-if="primaryCard.height"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.height") }}</label>
                <p class="info-value">
                  {{ primaryCard.height }} {{ $t("common.cm") || "cm" }}
                </p>
              </div>
              <div
                v-if="primaryCard.is_smoker !== undefined && primaryCard.is_smoker !== null"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.smoking") }}</label>
                <p class="info-value">
                  <b-badge :variant="primaryCard.is_smoker ? 'warning' : 'success'">
                    {{ primaryCard.is_smoker ? $t("common.yes") : $t("common.no") }}
                  </b-badge>
                </p>
              </div>
              <div
                v-if="primaryCard.has_diabetes !== undefined && primaryCard.has_diabetes !== null"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.diabetic") }}</label>
                <p class="info-value">
                  <b-badge :variant="primaryCard.has_diabetes ? 'danger' : 'success'">
                    {{ primaryCard.has_diabetes ? $t("common.yes") : $t("common.no") }}
                  </b-badge>
                </p>
              </div>
              <div
                v-if="primaryCard.has_heart_disease !== undefined && primaryCard.has_heart_disease !== null"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.heart-disease") }}</label>
                <p class="info-value">
                  <b-badge :variant="primaryCard.has_heart_disease ? 'danger' : 'success'">
                    {{ primaryCard.has_heart_disease ? $t("common.yes") : $t("common.no") }}
                  </b-badge>
                </p>
              </div>
              <div
                v-if="primaryCard.has_high_blood_pressure !== undefined && primaryCard.has_high_blood_pressure !== null"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.hypertension") }}</label>
                <p class="info-value">
                  <b-badge :variant="primaryCard.has_high_blood_pressure ? 'danger' : 'success'">
                    {{ primaryCard.has_high_blood_pressure ? $t("common.yes") : $t("common.no") }}
                  </b-badge>
                </p>
              </div>
              <div
                v-if="primaryCard.patient_case"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.patient-case") }}</label>
                <p class="info-value">
                  {{ primaryCard.patient_case }}
                </p>
              </div>
              <div
                v-if="primaryCard.diagnosis"
                class="info-row"
              >
                <label class="info-label">{{ $t("emergency.Diagnosis") }}</label>
                <p class="info-value">
                  {{ primaryCard.diagnosis }}
                </p>
              </div>
              <div
                v-if="primaryCard.allergy"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.allergy") }}</label>
                <p class="info-value">
                  {{ primaryCard.allergy }}
                </p>
              </div>

              <!-- Timestamps -->
              <div class="info-section-divider" />
              <div class="info-section-title">
                <feather-icon
                  icon="ClockIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("reception.patient-updates") }}
              </div>
              <div
                v-if="primaryCard.updated_at"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.date-of-last-update") }}</label>
                <p
                  class="info-value"
                  style="unicode-bidi:isolate"
                  :style="{ direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr' }"
                >
                  {{ primaryCard.updated_at }}
                </p>
              </div>
              <div
                v-if="primaryCard.created_at"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.patient-info-created-at") }}</label>
                <p
                  class="info-value"
                  style="unicode-bidi:isolate"
                  :style="{ direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr' }"
                >
                  {{ primaryCard.created_at }}
                </p>
              </div>
              <div
                v-if="primaryCard.out_date_time"
                class="info-row"
              >
                <label class="info-label">{{ $t("reception.out-date-time") }}</label>
                <p
                  class="info-value"
                  style="unicode-bidi:isolate"
                  :style="{ direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr' }"
                >
                  {{ primaryCard.out_date_time }}
                </p>
              </div>

            </div>
          </b-card-body>
        </b-card>

        <!-- ── Merged Data Card ──────────────────────────────────────── -->
        <b-card
          no-body
          class="info-card mb-3"
        >
          <b-card-body class="p-2">

            <!-- Tests -->
            <template v-if="$store.state.app.permissions.includes('get-patientCardTest')">
              <div class="info-section-title mt-1">
                <feather-icon
                  icon="DropletIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("lab.tests") }}
                <span class="count-badge ml-2">{{ allTests.length }}</span>
              </div>
              <div
                v-if="allTests.length === 0"
                class="no-data-empty"
              >
                <feather-icon
                  icon="DropletIcon"
                  size="36"
                  class="empty-icon"
                />
                <p>{{ $t("common.no-data-available") }}</p>
              </div>
              <div
                v-else
                class="modern-cards-grid"
              >
                <div
                  v-for="test in allTests"
                  :key="'test-' + test.id"
                  class="modern-card"
                >
                  <div class="modern-card-header">
                    <div class="modern-card-icon icon-primary">
                      <feather-icon
                        icon="DropletIcon"
                        size="20"
                      />
                    </div>
                    <div class="modern-card-title">
                      {{ test.test ? test.test.name : "-" }}
                    </div>
                  </div>
                  <div class="modern-card-content">
                    <div
                      v-if="test.sample_id"
                      class="modern-card-info-item"
                    >
                      <feather-icon
                        icon="HashIcon"
                        size="13"
                      />
                      <span>{{ $t("lab.sample-id") }}: {{ test.sample_id }}</span>
                    </div>
                    <div
                      v-if="test.result"
                      class="modern-card-info-item"
                    >
                      <feather-icon
                        icon="FileTextIcon"
                        size="13"
                      />
                      <span>{{ $t("lab.result") }}: {{ test.result }}</span>
                    </div>
                    <div
                      v-if="test.date"
                      class="modern-card-info-item"
                    >
                      <feather-icon
                        icon="CalendarIcon"
                        size="13"
                      />
                      <span>{{ test.date }}</span>
                    </div>
                  </div>
                  <div
                    v-if="test.group"
                    class="modern-card-footer"
                  >
                    <b-badge variant="info">
                      {{ $t("lab.test-group") }}: {{ test.group.name }}
                    </b-badge>
                  </div>
                </div>
              </div>
            </template>

            <!-- Drugs -->
            <div class="info-section-divider" />
            <div class="info-section-title">
              <feather-icon
                icon="PackageIcon"
                size="16"
                class="mr-1"
              />
              {{ $t("pharmacy.drugs") }}
              <span class="count-badge ml-2">{{ allDrugs.length }}</span>
            </div>
            <div
              v-if="allDrugs.length === 0"
              class="no-data-empty"
            >
              <feather-icon
                icon="PackageIcon"
                size="36"
                class="empty-icon"
              />
              <p>{{ $t("common.no-data-available") }}</p>
            </div>
            <div
              v-else
              class="modern-cards-grid"
            >
              <div
                v-for="drug in allDrugs"
                :key="'drug-' + drug.id"
                class="modern-card"
              >
                <div class="modern-card-header">
                  <div class="modern-card-icon icon-success">
                    <feather-icon
                      icon="PackageIcon"
                      size="20"
                    />
                  </div>
                  <div class="modern-card-title">
                    {{ drug.item ? drug.item.name : drug.item_name || "-" }}
                  </div>
                </div>
                <div class="modern-card-content">
                  <div
                    v-if="drug.quantity || drug.number_of_doses"
                    class="modern-card-info-item"
                  >
                    <feather-icon
                      icon="LayersIcon"
                      size="13"
                    />
                    <span>{{
                      drug.quantity
                        ? $t("pharmacy.Quantity") + ": " + drug.quantity
                        : $t("office.number_of_doses") + ": " + drug.number_of_doses
                    }}</span>
                  </div>
                  <div
                    v-if="drug.dose"
                    class="modern-card-info-item"
                  >
                    <feather-icon
                      icon="DropletIcon"
                      size="13"
                    />
                    <span>{{ $t("pharmacy.Dose") }}: {{ drug.dose }}</span>
                  </div>
                  <div
                    v-if="drug.duration_between_doses"
                    class="modern-card-info-item"
                  >
                    <feather-icon
                      icon="ClockIcon"
                      size="13"
                    />
                    <span>{{ $t("office.duration_between_doses") }}: {{ drug.duration_between_doses }}</span>
                  </div>
                  <div
                    v-if="drug.created_at"
                    class="modern-card-info-item"
                  >
                    <feather-icon
                      icon="CalendarIcon"
                      size="13"
                    />
                    <span>{{ drug.created_at }}</span>
                  </div>
                </div>
                <div
                  v-if="drug.source_of_drug"
                  class="modern-card-footer"
                >
                  <b-badge
                    :variant="drug.source_of_drug === 'hospital' ? 'primary' : drug.source_of_drug === 'pharmacy' ? 'success' : 'warning'"
                  >
                    {{ drug.source_of_drug }}
                  </b-badge>
                </div>
              </div>
            </div>

            <!-- Radiology -->
            <template v-if="$store.state.app.permissions.includes('get-patientCardRadiology')">
              <div class="info-section-divider" />
              <div class="info-section-title">
                <feather-icon
                  icon="ImageIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("radiology.radiology") }}
                <span class="count-badge ml-2">{{ allRadiology.length }}</span>
              </div>
              <div
                v-if="allRadiology.length === 0"
                class="no-data-empty"
              >
                <feather-icon
                  icon="ImageIcon"
                  size="36"
                  class="empty-icon"
                />
                <p>{{ $t("common.no-data-available") }}</p>
              </div>
              <div
                v-else
                class="modern-cards-grid"
              >
                <div
                  v-for="radiology in allRadiology"
                  :key="'rad-' + radiology.id"
                  class="modern-card"
                >
                  <div class="modern-card-header">
                    <div class="modern-card-icon icon-info">
                      <feather-icon
                        icon="ImageIcon"
                        size="20"
                      />
                    </div>
                    <div class="modern-card-title">
                      {{ radiology.radiology ? radiology.radiology.name : "-" }}
                    </div>
                  </div>
                  <div class="modern-card-content">
                    <div
                      v-if="radiology.url"
                      class="modern-card-info-item"
                    >
                      <feather-icon
                        icon="LinkIcon"
                        size="13"
                      />
                      <a
                        :href="radiology.url"
                        target="_blank"
                        class="text-primary"
                      >{{ $t("common.view") }}</a>
                    </div>
                    <div
                      v-if="radiology.created_at"
                      class="modern-card-info-item"
                    >
                      <feather-icon
                        icon="CalendarIcon"
                        size="13"
                      />
                      <span>{{ radiology.created_at }}</span>
                    </div>
                  </div>
                  <div
                    v-if="radiology.type"
                    class="modern-card-footer"
                  >
                    <b-badge variant="secondary">
                      {{ $t("common.type") }}: {{ radiology.type }}
                    </b-badge>
                  </div>
                </div>
              </div>
            </template>

            <!-- Blood Transfusions -->
            <div class="info-section-divider" />
            <div class="info-section-title">
              <feather-icon
                icon="DropletIcon"
                size="16"
                class="mr-1"
              />
              {{ $t("bloodBank.blood-transfusions") }}
              <span class="count-badge ml-2">{{ allBloodTransfusions.length }}</span>
            </div>
            <div
              v-if="allBloodTransfusions.length === 0"
              class="no-data-empty"
            >
              <feather-icon
                icon="DropletIcon"
                size="36"
                class="empty-icon"
              />
              <p>{{ $t("common.no-data-available") }}</p>
            </div>
            <div
              v-else
              class="modern-cards-grid"
            >
              <div
                v-for="transfusion in allBloodTransfusions"
                :key="'bt-' + transfusion.id"
                class="modern-card"
              >
                <div class="modern-card-header">
                  <div class="modern-card-icon icon-danger">
                    <feather-icon
                      icon="DropletIcon"
                      size="20"
                    />
                  </div>
                  <div class="modern-card-title">
                    {{ getTransfusionType(transfusion.blood_transfusion_type_id) }}
                  </div>
                </div>
                <div class="modern-card-content">
                  <div class="modern-card-info-item">
                    <feather-icon
                      icon="TagIcon"
                      size="13"
                    />
                    <span>{{ $t("bloodBank.patient-abo") }}: {{ transfusion.patient_abo }}</span>
                  </div>
                  <div class="modern-card-info-item">
                    <feather-icon
                      icon="TagIcon"
                      size="13"
                    />
                    <span>{{ $t("bloodBank.patient-rh") }}: {{ transfusion.patient_rh }}</span>
                  </div>
                </div>
                <div class="modern-card-footer">
                  <b-badge variant="info">
                    {{ $t("bloodBank.number-of-units") }}: {{ transfusion.number_of_units }}
                  </b-badge>
                </div>
              </div>
            </div>

            <!-- Invoices -->
            <div class="info-section-divider" />
            <div class="info-section-title">
              <feather-icon
                icon="FileTextIcon"
                size="16"
                class="mr-1"
              />
              {{ $t("incubator.invoices") }}
              <span class="count-badge ml-2">{{ allInvoices.length }}</span>
            </div>
            <div
              v-if="allInvoices.length === 0"
              class="no-data-empty"
            >
              <feather-icon
                icon="FileTextIcon"
                size="36"
                class="empty-icon"
              />
              <p>{{ $t("common.no-invoices-found") }}</p>
            </div>
            <div
              v-else
              class="invoices-grid"
            >
              <div
                v-for="invoice in allInvoices"
                :key="'inv-' + invoice.id"
                class="invoice-card"
              >
                <div class="invoice-header">
                  <div class="invoice-number">
                    #{{ invoice.invoice_no }}
                  </div>
                  <b-badge :variant="getInvoiceStatusVariant(invoice.status)">
                    {{ invoice.status }}
                  </b-badge>
                </div>
                <div class="invoice-amount">
                  {{ formatPrice(invoice.amount) }}
                </div>
                <div
                  v-if="invoice.description"
                  class="invoice-description"
                >
                  {{ invoice.description }}
                </div>
                <div class="invoice-footer">
                  <div class="invoice-date">
                    <feather-icon
                      icon="CalendarIcon"
                      size="13"
                    />
                    <span>{{ invoice.date || formatDateTime(invoice.created_at) }}</span>
                  </div>
                  <div class="invoice-type">
                    <feather-icon
                      icon="TagIcon"
                      size="13"
                    />
                    <span>{{ getInvoiceTypeName(invoice.type) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Attachments (one per card, stacked) -->
            <div class="info-section-divider" />
            <div class="info-section-title">
              <feather-icon
                icon="PaperclipIcon"
                size="16"
                class="mr-1"
              />
              {{ $t("common.attachments") }}
            </div>
            <div
              v-for="card in patientCardsData"
              :key="'att-' + card.id"
            >
              <Attachments
                :patient-card-id="card.id"
                :is-history="true"
              />
            </div>

          </b-card-body>
        </b-card>

        <!-- Empty state -->
        <div
          v-if="!loading && patientCardsData.length === 0"
          class="no-data-empty"
        >
          <feather-icon
            icon="FolderIcon"
            size="48"
            class="empty-icon"
          />
          <p>{{ $t("common.no-data-available") }}</p>
        </div>

      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BCardBody, BCardHeader, BBadge,
} from 'bootstrap-vue'
import Attachments from '@/views/Reception/Attachments.vue'

export default {
  components: {
    BCard, BRow, BCol, BCardBody, BCardHeader, BBadge, Attachments,
  },

  data() {
    return {
      loading: false,
      patientCardsData: [],
      // flat merged arrays – populated by pushing from every card's fetch
      allTests: [],
      allDrugs: [],
      allRadiology: [],
      allBloodTransfusions: [],
      allInvoices: [],
      transfusionTypes: [],
    }
  },

  computed: {
    // The header always shows data from the first (most recent) card only
    primaryCard() {
      return this.patientCardsData[0] || null
    },
  },

  mounted() {
    this.getPatientCards()
    this.getTransfusionTypeList()
  },

  methods: {
    // ── Fetch cards then load all related data in parallel ────────────
    async getPatientCards() {
      this.loading = true
      await this.getPatient(
        `/patientCard?include=patient&take=20&filter[patient_id]=${this.$route.params.id}&${
          this.$route.params.date ? `filter[created_at]=${this.$route.params.date}` : ''
        }`,
      )
      this.loading = false
      this.patientCardsData = this.all_data.data || []

      // Fetch everything for all cards simultaneously, results merged into flat arrays
      await Promise.all(
        this.patientCardsData.map(card => this.fetchAllRelatedData(card.id)),
      )
    },

    async fetchAllRelatedData(cardId) {
      await Promise.all([
        this.fetchTests(cardId),
        this.fetchDrugs(cardId),
        this.fetchRadiology(cardId),
        this.fetchBloodTransfusions(cardId),
        this.fetchInvoices(cardId),
      ])
    },

    // Each fetcher appends into the shared flat array
    async fetchTests(cardId) {
      try {
        await this.getList(
          `/patientCardTest?filter[patient_card_id]=${cardId}&include=test,group,unit&sort=-id&take=100`,
        )
        this.allTests.push(...(this.all_data.data || []))
      } catch { /* silent */ }
    },

    async fetchDrugs(cardId) {
      try {
        await this.getList(
          `/patientCardDrug?filter[patient_card_id]=${cardId}&include=item&sort=-id&take=100`,
        )
        const pharmacy = this.all_data.data || []

        await this.getList(
          `/patientCardPrescriptions?filter[patient_card_id]=${cardId}&include=item&sort=-id&take=100`,
        )
        const prescriptions = this.all_data.data || []

        this.allDrugs.push(...pharmacy, ...prescriptions)
      } catch { /* silent */ }
    },

    async fetchRadiology(cardId) {
      try {
        await this.getList(
          `/patientCardRadiology?filter[patient_card_id]=${cardId}&include=radiology&sort=-id&take=100`,
        )
        this.allRadiology.push(...(this.all_data.data || []))
      } catch { /* silent */ }
    },

    async fetchBloodTransfusions(cardId) {
      try {
        await this.getList(
          `/bloodTransfusions?filter[patient_card_id]=${cardId}&include[]=transfusionType`,
        )
        this.allBloodTransfusions.push(...(this.all_data.data || []))
      } catch { /* silent */ }
    },

    async fetchInvoices(cardId) {
      try {
        await this.getList(
          `/invoices?filter[patient_card_id]=${cardId}&include=patientCard&sort=-id`,
        )
        this.allInvoices.push(
          ...(Array.isArray(this.all_data?.data) ? this.all_data.data : []),
        )
      } catch { /* silent */ }
    },

    async getTransfusionTypeList() {
      try {
        await this.getList('/bloodTransfusionTypes')
        this.transfusionTypes = this.all_data.data || []
      } catch { /* silent */ }
    },

    // ── Helpers ───────────────────────────────────────────────────────
    getTransfusionType(id) {
      if (!id || !this.transfusionTypes.length) return '-'
      const t = this.transfusionTypes.find(x => x.id === id)
      return t ? t.name : '-'
    },
    getInvoiceStatusVariant(status) {
      return (
        {
          paid: 'success', pending: 'warning', cancelled: 'danger', partial: 'info', approved: 'success', printed: 'info',
        }[status] || 'secondary'
      )
    },
    getInvoiceTypeName(type) {
      const map = {
        invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
        invoice_nicu: 'cashiers.invoice-type-nicu',
        invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
        refund: 'cashiers.invoice-type-refund',
        invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
        invoice_emergency: 'cashiers.invoice-type-emergency',
      }
      return map[type] ? this.$t(map[type]) : type
    },
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : '0 IQD'
    },
    formatDateTime(str) {
      if (!str) return ''
      const d = new Date(str)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
  },
}
</script>

<style scoped>
/* ── Card shell ── */
.info-card {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0,0,0,.05);
  transition: box-shadow .2s ease;
}
.info-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,.1); }

/* ── Section header ── */
.section-header {
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: .625rem 1rem;
  display: flex;
  align-items: center;
}
.section-header h5 {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: .25rem;
  margin-bottom: 0;
}

/* ── Info rows ── */
.info-list { display: flex; flex-direction: column; gap: .75rem; }
.info-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding-bottom: .75rem;
  border-bottom: 1px solid #f3f4f6;
}
.info-row:last-child { border-bottom: none; padding-bottom: 0; }
.info-label {
  font-size: .875rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 180px;
  flex-shrink: 0;
  margin-bottom: 0;
  display: block;
}
.info-value {
  font-size: .9375rem;
  color: #111827;
  margin-bottom: 0;
  word-break: break-word;
  line-height: 1.4;
  flex: 1;
}

/* ── Dividers & section titles ── */
.info-section-divider {
  margin: 1rem 0;
  border-top: 2px solid #e5e7eb;
}
.info-section-title {
  font-size: .875rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: .75rem;
  display: flex;
  align-items: center;
  gap: .5rem;
}

/* ── Count badge ── */
.count-badge {
  background: #667eea;
  color: #fff;
  font-size: .72rem;
  font-weight: 600;
  padding: .15rem .5rem;
  border-radius: 12px;
}

/* ── Modern cards grid ── */
.modern-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
  margin-top: .5rem;
}
.modern-card {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  transition: all .25s;
  display: flex;
  flex-direction: column;
}
.modern-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(0,0,0,.09);
  border-color: #667eea;
}
.modern-card-header {
  display: flex;
  align-items: center;
  gap: .625rem;
  margin-bottom: .75rem;
}
.modern-card-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
}
.icon-primary { background: #667eea; }
.icon-success { background: #22c55e; }
.icon-info    { background: #0ea5e9; }
.icon-danger  { background: #ef4444; }

.modern-card-title {
  font-size: .9375rem;
  font-weight: 700;
  color: #0f172a;
  flex: 1;
  line-height: 1.3;
}
.modern-card-content { flex: 1; margin-bottom: .75rem; }
.modern-card-info-item {
  display: flex;
  align-items: center;
  gap: .4rem;
  font-size: .8125rem;
  color: #64748b;
  margin-bottom: .4rem;
}
.modern-card-info-item:last-child { margin-bottom: 0; }
.modern-card-info-item a { color: #667eea; text-decoration: none; }
.modern-card-info-item a:hover { text-decoration: underline; }
.modern-card-footer {
  display: flex;
  align-items: center;
  padding-top: .625rem;
  border-top: 1px dashed #cbd5e1;
  gap: .5rem;
}

/* ── Invoices ── */
.invoices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
  margin-top: .5rem;
}
.invoice-card {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  transition: all .25s;
}
.invoice-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(0,0,0,.09);
  border-color: #667eea;
}
.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: .75rem;
}
.invoice-number { font-size: .9375rem; font-weight: 700; color: #0f172a; }
.invoice-amount { font-size: 1.375rem; font-weight: 700; color: #22c55e; margin-bottom: .625rem; }
.invoice-description { font-size: .8125rem; color: #64748b; margin-bottom: .625rem; line-height: 1.5; }
.invoice-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: .625rem;
  border-top: 1px dashed #cbd5e1;
  gap: .5rem;
}
.invoice-date,
.invoice-type {
  display: flex;
  align-items: center;
  gap: .4rem;
  font-size: .8125rem;
  color: #64748b;
}

/* ── Empty state ── */
.no-data-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #94a3b8;
}
.no-data-empty .empty-icon { opacity: .3; margin-bottom: .75rem; }
.no-data-empty p { margin: 0; font-size: .9375rem; }
</style>
